"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DashboardView = void 0;
var tslib_1 = require("tslib");
// (C) 2020 GoodData Corporation
var react_1 = tslib_1.__importStar(require("react"));
var sdk_ui_1 = require("@gooddata/sdk-ui");
var sdk_ui_theme_provider_1 = require("@gooddata/sdk-ui-theme-provider");
var sdk_backend_spi_1 = require("@gooddata/sdk-backend-spi");
var sdk_model_1 = require("@gooddata/sdk-model");
var internal_1 = require("../internal");
var hooks_1 = require("./hooks");
var internal_2 = require("./hooks/internal");
var ScheduledMailDialog_1 = require("./ScheduledMailDialog");
var defaultDashboardThemeModifier_1 = require("./defaultDashboardThemeModifier");
var DashboardRenderer_1 = require("./DashboardRenderer");
var EmptyDashboardError_1 = require("./EmptyDashboardError");
var DashboardViewProvider_1 = require("./DashboardViewProvider");
var defaultEmptyLayout = {
    type: "IDashboardLayout",
    sections: [],
};
exports.DashboardView = function (_a) {
    var _b, _c, _d, _e, _f, _g;
    var dashboard = _a.dashboard, filters = _a.filters, onFiltersChange = _a.onFiltersChange, theme = _a.theme, _h = _a.disableThemeLoading, disableThemeLoading = _h === void 0 ? false : _h, _j = _a.themeModifier, themeModifier = _j === void 0 ? defaultDashboardThemeModifier_1.defaultDashboardThemeModifier : _j, backend = _a.backend, workspace = _a.workspace, onDrill = _a.onDrill, drillableItems = _a.drillableItems, onError = _a.onError, onDashboardLoaded = _a.onDashboardLoaded, config = _a.config, isScheduledMailDialogVisible = _a.isScheduledMailDialogVisible, _k = _a.applyFiltersToScheduledMail, applyFiltersToScheduledMail = _k === void 0 ? true : _k, onScheduledMailDialogCancel = _a.onScheduledMailDialogCancel, onScheduledMailDialogSubmit = _a.onScheduledMailDialogSubmit, onScheduledMailSubmitError = _a.onScheduledMailSubmitError, onScheduledMailSubmitSuccess = _a.onScheduledMailSubmitSuccess, _l = _a.ErrorComponent, ErrorComponent = _l === void 0 ? sdk_ui_1.ErrorComponent : _l, _m = _a.LoadingComponent, LoadingComponent = _m === void 0 ? sdk_ui_1.LoadingComponent : _m, widgetRenderer = _a.widgetRenderer, transformLayout = _a.transformLayout, _o = _a.isReadOnly, isReadOnly = _o === void 0 ? false : _o, execConfig = _a.execConfig;
    var dashboardRef = typeof dashboard === "string" ? sdk_model_1.idRef(dashboard, "analyticalDashboard") : dashboard;
    var _p = hooks_1.useDashboard({
        dashboard: dashboardRef,
        backend: backend,
        workspace: workspace,
    }), dashboardError = _p.error, dashboardData = _p.result, dashboardStatus = _p.status;
    var _q = hooks_1.useDashboardAlerts({
        dashboard: dashboardRef,
        backend: backend,
        workspace: workspace,
    }), alertsError = _q.error, alertsData = _q.result, alertsStatus = _q.status;
    var _r = internal_2.useUserWorkspaceSettings({
        backend: backend,
        workspace: workspace,
    }), userWorkspaceSettingsError = _r.error, userWorkspaceSettings = _r.result, userWorkspaceSettingsStatus = _r.status;
    var _s = internal_2.useColorPalette({
        backend: backend,
        workspace: workspace,
    }), colorPaletteError = _s.error, colorPalette = _s.result, colorPaletteStatus = _s.status;
    var _t = internal_2.useAttributesWithDrillDown({
        hasDrillingEnabled: !!onDrill,
        backend: backend,
        workspace: workspace,
    }), drillDownAttributesError = _t.error, drillDownAttributes = _t.result, drillDownAttributesStatus = _t.status;
    var _u = internal_2.useDashboardLayoutData({
        // With new delete insight functionality, dashboards layout can now be empty.
        // Use an empty layout to avoid the permanent 'pending' status.
        dashboardLayout: (_b = dashboardData === null || dashboardData === void 0 ? void 0 : dashboardData.layout) !== null && _b !== void 0 ? _b : defaultEmptyLayout,
        backend: backend,
        workspace: workspace,
    }), dashboardLayoutError = _u.error, dashboardLayoutResult = _u.result, dashboardLayoutStatus = _u.status;
    var error = (_f = (_e = (_d = (_c = dashboardError !== null && dashboardError !== void 0 ? dashboardError : alertsError) !== null && _c !== void 0 ? _c : userWorkspaceSettingsError) !== null && _d !== void 0 ? _d : colorPaletteError) !== null && _e !== void 0 ? _e : drillDownAttributesError) !== null && _f !== void 0 ? _f : dashboardLayoutError;
    var isThemeLoading = sdk_ui_theme_provider_1.useThemeIsLoading();
    var hasThemeProvider = isThemeLoading !== undefined;
    react_1.useEffect(function () {
        if (dashboardData &&
            alertsData &&
            userWorkspaceSettings &&
            colorPalette &&
            drillDownAttributes &&
            dashboardLayoutResult) {
            onDashboardLoaded === null || onDashboardLoaded === void 0 ? void 0 : onDashboardLoaded({
                alerts: alertsData,
                dashboard: dashboardData,
            });
        }
    }, [
        onDashboardLoaded,
        dashboardData,
        alertsData,
        userWorkspaceSettings,
        colorPalette,
        drillDownAttributes,
        dashboardLayoutResult,
    ]);
    react_1.useEffect(function () {
        if (error && onError) {
            onError(error);
        }
    }, [onError, error]);
    var effectiveLocale = (_g = config === null || config === void 0 ? void 0 : config.locale) !== null && _g !== void 0 ? _g : userWorkspaceSettings === null || userWorkspaceSettings === void 0 ? void 0 : userWorkspaceSettings.locale;
    var effectiveConfig = react_1.useMemo(function () {
        var _a;
        if (!config && !userWorkspaceSettings) {
            return undefined;
        }
        return {
            mapboxToken: config === null || config === void 0 ? void 0 : config.mapboxToken,
            separators: (_a = config === null || config === void 0 ? void 0 : config.separators) !== null && _a !== void 0 ? _a : userWorkspaceSettings === null || userWorkspaceSettings === void 0 ? void 0 : userWorkspaceSettings.separators,
            disableKpiDrillUnderline: userWorkspaceSettings === null || userWorkspaceSettings === void 0 ? void 0 : userWorkspaceSettings.disableKpiDashboardHeadlineUnderline,
        };
    }, [config, userWorkspaceSettings]);
    var sanitizedFilters = react_1.useMemo(function () {
        return filters ? internal_1.filterArrayToFilterContextItems(filters) : undefined;
    }, [filters]);
    var statuses = [
        dashboardStatus,
        alertsStatus,
        userWorkspaceSettingsStatus,
        colorPaletteStatus,
        drillDownAttributesStatus,
        dashboardLayoutStatus,
    ];
    if (error) {
        return react_1.default.createElement(ErrorComponent, { message: error.message });
    }
    if (statuses.includes("loading") || statuses.includes("pending")) {
        return react_1.default.createElement(LoadingComponent, null);
    }
    var dashboardRender = (react_1.default.createElement(DashboardViewProvider_1.DashboardViewProvider, { config: effectiveConfig, settings: userWorkspaceSettings, colorPalette: colorPalette, drillDownAttributes: drillDownAttributes, alerts: alertsData, isReadOnly: isReadOnly, locale: effectiveLocale, execConfig: execConfig },
        !isReadOnly && (react_1.default.createElement(ScheduledMailDialog_1.ScheduledMailDialog, { backend: backend, workspace: workspace, locale: effectiveLocale, dashboard: dashboardRef, filters: applyFiltersToScheduledMail ? sanitizedFilters : undefined, onSubmit: onScheduledMailDialogSubmit, onSubmitSuccess: onScheduledMailSubmitSuccess, onSubmitError: onScheduledMailSubmitError, onCancel: onScheduledMailDialogCancel, onError: onError, isVisible: isScheduledMailDialogVisible })),
        !dashboardData.layout || sdk_backend_spi_1.isDashboardLayoutEmpty(dashboardData.layout) ? (react_1.default.createElement(EmptyDashboardError_1.EmptyDashboardError, { ErrorComponent: ErrorComponent })) : (react_1.default.createElement(DashboardRenderer_1.DashboardRenderer, { transformLayout: transformLayout, backend: backend, workspace: workspace, dashboardRef: dashboardRef, dashboardLayout: dashboardData === null || dashboardData === void 0 ? void 0 : dashboardData.layout, filters: sanitizedFilters, onFiltersChange: onFiltersChange, filterContext: dashboardData.filterContext, onDrill: onDrill, drillableItems: drillableItems, ErrorComponent: ErrorComponent, LoadingComponent: LoadingComponent, className: "gd-dashboards-root gd-dashboard-view", getVisType: dashboardLayoutResult.getVisType, getInsightByRef: dashboardLayoutResult.getInsightByRef, widgetRenderer: widgetRenderer, areSectionHeadersEnabled: userWorkspaceSettings === null || userWorkspaceSettings === void 0 ? void 0 : userWorkspaceSettings.enableSectionHeaders }))));
    if (theme || (!hasThemeProvider && !disableThemeLoading)) {
        dashboardRender = (react_1.default.createElement(sdk_ui_theme_provider_1.ThemeProvider, { theme: theme, backend: backend, workspace: workspace, modifier: themeModifier }, dashboardRender));
    }
    return dashboardRender;
};
//# sourceMappingURL=DashboardView.js.map